import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Brain, Activity, Zap, Sparkles, Users } from "lucide-react";

const AnimatedBlock = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const pillars = [
  {
    num: "1",
    icon: Brain,
    title: "MENTE",
    subtitle: "Riscrivere la tua identit\u00e0 interiore",
    intro: "Qui inizia tutto. La tua mente \u00e8 il filtro attraverso cui interpreti la realt\u00e0. Se non la alleni, continuer\u00e0 a riprodurre schemi, paure e convinzioni che non ti appartengono.",
    body: "Il Guerriero di Luce sviluppa una mente lucida, presente, consapevole. Impara a osservare senza reagire automaticamente. Smette di vivere in modalit\u00e0 sopravvivenza e inizia a scegliere intenzionalmente.",
    items: ["Consapevolezza mentale", "Riprogrammazione delle credenze", "Presenza e osservazione", "Responsabilit\u00e0 personale"],
    closing: "Perch\u00e9 quando cambi la mente, cambi la direzione della tua vita.",
  },
  {
    num: "2",
    icon: Activity,
    title: "CORPO",
    subtitle: "Regolare il sistema nervoso e tornare alla sicurezza",
    intro: "Non puoi incarnare luce in un corpo che vive in allerta. Il tuo sistema nervoso \u00e8 la base di tutto.",
    body: "Il Guerriero di Luce non fugge dal corpo. Ci entra. Impara ad ascoltarlo, a sentirlo, a rispettarlo. Regolare il sistema nervoso significa creare uno stato interno di sicurezza.",
    items: ["Respirazione consapevole", "Rilascio delle tensioni e traumi somatici", "Radicamento (grounding)", "Presenza nel corpo"],
    closing: "Perch\u00e9 non puoi sostenere una vita espansa se il tuo corpo \u00e8 ancora in modalit\u00e0 sopravvivenza.",
  },
  {
    num: "3",
    icon: Zap,
    title: "ENERGETICA",
    subtitle: "Attivare e allineare la tua frequenza",
    intro: "Tutto \u00e8 energia. E tu non attrai ci\u00f2 che vuoi\u2026 attrai ci\u00f2 che sei a livello energetico.",
    body: "Il Guerriero di Luce comprende le leggi invisibili che governano la realt\u00e0. Lavorare sul piano energetico significa: ripulire, attivare, proteggere e dirigere la tua energia.",
    items: ["Attivazioni energetiche", "Visualizzazioni guidate", "Lavoro sui centri energetici (chakra)", "Pulizia e protezione energetica"],
    closing: "Quando sei allineata energeticamente, la realt\u00e0 inizia a rispondere.",
  },
  {
    num: "4",
    icon: Sparkles,
    title: "SPIRITUALIT\u00c0",
    subtitle: "Connessione, allineamento e guida dell'anima",
    intro: "La spiritualit\u00e0 non \u00e8 qualcosa di esterno. \u00c8 il ritorno a casa dentro di te.",
    body: "Il Guerriero di Luce coltiva una connessione profonda con la propria anima. Impara ad ascoltare una guida pi\u00f9 sottile. Quella voce silenziosa che sa, che orienta, che vede oltre.",
    items: ["Ascolto interiore", "Fiducia nell'intuizione", "Allineamento tra anima, mente e azione", "Connessione con qualcosa di pi\u00f9 grande"],
    closing: "Quando sei allineata alla tua anima, le decisioni diventano pi\u00f9 chiare, i percorsi pi\u00f9 fluidi.",
  },
  {
    num: "5",
    icon: Users,
    title: "AMBIENTE & COMMUNITY",
    subtitle: "La frequenza delle persone che ti circondano",
    intro: "Non puoi crescere in un ambiente che nutre la tua versione limitata.",
    body: "Il Guerriero di Luce sceglie consapevolmente il proprio ambiente. Hai bisogno di una community che rifletta la tua luce, supporti la tua evoluzione e ti veda davvero.",
    items: ["Connessioni autentiche", "Spazi sicuri", "Relazioni allineate ai tuoi valori"],
    closing: "Quando sei circondata da persone nella tua stessa frequenza, puoi semplicemente essere. E crescere.",
  },
];

const ValuesSection = () => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="values-section" className="py-24 md:py-32 bg-[#ECEAE6] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#E1C340]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

      <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
        <AnimatedBlock>
          <div className="text-center mb-6">
            <p className="text-sm uppercase tracking-[0.2em] text-[#AC8E60] font-body font-semibold mb-4">
              Light Workers House
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#442F23] mb-4">
              I 5 Pilastri per Risvegliare e Incarnare il tuo Guerriero di Luce
            </h2>
            <div className="section-divider mb-6" />
          </div>
        </AnimatedBlock>

        <AnimatedBlock delay={0.1}>
          <p className="font-body text-base text-[#442F23]/70 text-center max-w-3xl mx-auto mb-16 leading-relaxed">
            <strong>Ti presento le fondamenta della casa del Guerriero di Luce.</strong>
            {" "}Man mano che integri questi 5 pilastri, smetti di cercare fuori ci&ograve; che sei destinata/o a diventare dentro.
            Non si tratta di diventare qualcuno. Si tratta di ricordare, attivare e incarnare chi sei davvero:
            <strong> un Guerriero di Luce.</strong>
          </p>
        </AnimatedBlock>

        <div className="space-y-8">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <AnimatedBlock key={p.title} delay={i * 0.08}>
                <div className="bg-[#FBF8E9] border border-[#AC8E60]/10 rounded-[2rem] p-8 lg:p-10" data-testid={`pillar-${i}`}>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-full bg-[#E1C340]/15 flex items-center justify-center flex-shrink-0">
                      <Icon size={22} className="text-[#AC8E60]" />
                    </div>
                    <div>
                      <span className="font-body text-xs uppercase tracking-[0.15em] text-[#AC8E60] font-semibold">Pilastro {p.num}</span>
                      <h3 className="font-heading text-xl font-semibold text-[#442F23]">{p.title} <span className="text-[#AC8E60] font-normal text-lg">&mdash; {p.subtitle}</span></h3>
                    </div>
                  </div>
                  <p className="font-body text-sm text-[#442F23]/75 leading-relaxed mb-3">{p.intro}</p>
                  <p className="font-body text-sm text-[#442F23]/70 leading-relaxed mb-4">{p.body}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 font-body text-sm text-[#442F23]/65">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E1C340] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="font-body text-sm text-[#AC8E60] font-medium italic">{p.closing}</p>
                </div>
              </AnimatedBlock>
            );
          })}
        </div>

        {/* Integration block */}
        <AnimatedBlock delay={0.5}>
          <div className="mt-12 text-center py-10 border-t border-[#AC8E60]/15">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#442F23] mb-4">
              Integrazione: Diventare il Guerriero di Luce
            </h3>
            <p className="font-body text-base text-[#442F23]/70 max-w-2xl mx-auto leading-relaxed mb-8">
              Quando lavori su tutti e cinque: la tua mente diventa chiara, il tuo corpo diventa stabile,
              la tua energia diventa potente, la tua anima diventa guida, il tuo ambiente diventa nutrimento.
              A quel punto&hellip; non stai pi&ugrave; cercando la tua strada.
              <strong> Sei tu la strada.</strong>
            </p>
            <button
              onClick={() => scrollTo("pricing-section")}
              className="btn-primary text-base"
              data-testid="pillars-cta"
            >
              Voglio Iscrivermi
            </button>
          </div>
        </AnimatedBlock>
      </div>
    </section>
  );
};

export default ValuesSection;
